/**
 * Labstep
 *
 * @module components/Folder/Action/Share
 * @desc Custom Share action for Folder that handles is_sharing_enabled logic
 */

import { Can } from 'labstep-web/components/Entity/Can';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { PermissionManager } from 'labstep-web/components/Permission/Manager';
import { ShareLinkActionCreate } from 'labstep-web/components/ShareLink/Action/Create';
import { ShareLinkInfo } from 'labstep-web/components/ShareLink/Info';
import { EntityUpdateContainer } from 'labstep-web/containers/Entity/Update';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import type { ActionComponentProps } from 'labstep-web/core/Action/Component/types';
import { Flex } from 'labstep-web/core/Flex';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import {
  type Folder,
  FolderType,
} from 'labstep-web/models/folder.model';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import type React from 'react';
import { useState } from 'react';

interface FolderActionShareProps {
  folder: Folder;
  actionComponentProps?: ActionComponentProps;
}

export const FolderActionShare: React.FC<FolderActionShareProps> = ({
  folder,
  actionComponentProps,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(
    null,
  );

  const handleUpdateSharingEnabled = (
    updateFolder: (body: any, options?: any) => void,
  ) => {
    updateFolder(
      { is_sharing_enabled: true },
      {
        onSuccess: () => {
          setErrorMessage(null);
          // The folder is now updated, the ShareLinkActionCreate will handle the rest
        },
        onError: (error: any) => {
          if (error?.response?.status === 403) {
            setErrorMessage(
              'There are some experiments in this folder which you may not have permission to share. Please remove in order to share this folder',
            );
          } else {
            setErrorMessage(
              'An error occurred while updating the folder',
            );
          }
        },
      },
    );
  };

  return (
    <Can
      entityName={folder.entityName}
      id={folder.idAttr}
      action={Action.share}
    >
      <Modal
        size="small"
        header={`Share ${getHumanReadableEntityName(
          folder.entityName,
          false,
          true,
        )}`}
        viewComponent={({ toggleModal }) => (
          <ActionComponent
            type="option"
            onClick={toggleModal}
            icon="share alternate"
            text="Share"
            {...actionComponentProps}
          />
        )}
        content={
          <EntityUpdateContainer
            entityName={folder.entityName}
            id={folder.id}
          >
            {({ update: updateFolder }) => (
              <Flex spacing="gap" column>
                {errorMessage && (
                  <div
                    style={{
                      color: 'red',
                      marginBottom: '1rem',
                      padding: '0.5rem',
                      backgroundColor: '#ffeaea',
                      border: '1px solid #ffcccc',
                      borderRadius: '4px',
                    }}
                  >
                    {errorMessage}
                  </div>
                )}
                {folder.is_sharing_enabled ? (
                  <ShareLinkActionCreate parentEntity={folder}>
                    {({ shareLink }) => (
                      <Flex spacing="gap" column>
                        <ShareLinkInfo
                          shareLink={shareLink}
                          parentEntity={folder}
                        />
                        <PermissionManager parentEntity={folder} />
                      </Flex>
                    )}
                  </ShareLinkActionCreate>
                ) : (
                  (() => {
                    // Determine entity name and message based on folder type
                    let entityName: string;
                    let entityDisplayName: string;

                    if (
                      folder.type === FolderType.experiment_workflow
                    ) {
                      entityName = ExperimentWorkflow.entityName;
                      entityDisplayName =
                        ExperimentWorkflow.getHumanReadableEntityName(
                          true,
                          false,
                        );
                    } else if (
                      folder.type === FolderType.protocol_collection
                    ) {
                      entityName = ProtocolCollection.entityName;
                      entityDisplayName =
                        ProtocolCollection.getHumanReadableEntityName(
                          true,
                          false,
                        );
                    } else {
                      return null;
                    }

                    return (
                      <ReadOnMountHOC
                        type="count"
                        entityName={entityName}
                        params={{
                          folder_id: folder.id,
                          owner_not_id: folder.owner.id,
                          group_id: folder.owner.id,
                        }}
                      >
                        {({ total }) => {
                          if (total > 0) {
                            return (
                              <div
                                style={{
                                  color: 'red',
                                  padding: '1rem',
                                  backgroundColor: '#ffeaea',
                                  border: '1px solid #ffcccc',
                                  borderRadius: '4px',
                                }}
                              >
                                There are {entityDisplayName} in this
                                folder not owned by the current
                                workspace therefore you cannot share
                                this collection.
                              </div>
                            );
                          }
                          return (
                            <div>
                              <p>
                                Click below to enable sharing for this
                                collection.
                              </p>
                              <ActionComponent
                                type="button"
                                onClick={() =>
                                  handleUpdateSharingEnabled(
                                    updateFolder,
                                  )
                                }
                                icon="share alternate"
                                text="Enable Sharing"
                              />
                            </div>
                          );
                        }}
                      </ReadOnMountHOC>
                    );
                  })()
                )}
              </Flex>
            )}
          </EntityUpdateContainer>
        }
        actions={[]}
      />
    </Can>
  );
};
